import i18n from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next'

export const locales = ['en', 'es', 'pt', 'de', 'fr', 'it', 'ru', 'zh', 'ja', 'ko'] as const
export type Locale = (typeof locales)[number]

export const localeMeta: Record<Locale, { label: string; shortLabel: string; htmlLang: string }> = {
  en: { label: 'English', shortLabel: 'EN', htmlLang: 'en' },
  es: { label: 'Español', shortLabel: 'ES', htmlLang: 'es' },
  pt: { label: 'Português (Brasil)', shortLabel: 'PT', htmlLang: 'pt-BR' },
  de: { label: 'Deutsch', shortLabel: 'DE', htmlLang: 'de' },
  fr: { label: 'Français', shortLabel: 'FR', htmlLang: 'fr' },
  it: { label: 'Italiano', shortLabel: 'IT', htmlLang: 'it' },
  ru: { label: 'Русский', shortLabel: 'RU', htmlLang: 'ru' },
  zh: { label: '简体中文', shortLabel: 'ZH', htmlLang: 'zh-Hans' },
  ja: { label: '日本語', shortLabel: 'JA', htmlLang: 'ja' },
  ko: { label: 'Korean', shortLabel: 'KO', htmlLang: 'ko' },
}

let availableArticleLocales: ReadonlySet<Locale> | null = null

export function setAvailableArticleLocales(values: Iterable<Locale> | null) {
  availableArticleLocales = values ? new Set(values) : null
}

export function isLocale(value: string | undefined): value is Locale {
  return Boolean(value && locales.includes(value.toLowerCase() as Locale))
}

export function localeFromPath(pathname = window.location.pathname): Locale | null {
  const segment = pathname.split('/').filter(Boolean)[0]?.toLowerCase()
  return isLocale(segment) ? segment : null
}

export function localeRootPath(locale: Locale): string {
  return locale === 'en' ? '/' : `/${locale}/`
}

export function pathWithoutLocale(pathname: string): string {
  const locale = localeFromPath(pathname)
  if (!locale) return pathname || '/'
  const stripped = pathname.slice(`/${locale}`.length)
  return stripped || '/'
}

export function localizedRoutePath(locale: Locale, pathname: string): string {
  const route = pathWithoutLocale(pathname)
  if (route === '/') return localeRootPath(locale)
  if (/^\/articles\/[a-z0-9]+(?:-[a-z0-9]+)*\/?$/.test(route) && availableArticleLocales && !availableArticleLocales.has(locale)) {
    return articlesPath(locale)
  }
  return `${locale === 'en' ? '' : `/${locale}`}${route}`
}

export function localizedPath(locale: Locale): string {
  const hash = window.location.hash
  const search = window.location.search
  return `${localizedRoutePath(locale, window.location.pathname)}${search}${hash}`
}

export function articlesPath(locale: Locale): string {
  return `${locale === 'en' ? '' : `/${locale}`}/articles/`
}

export function articlePath(locale: Locale, slug: string): string {
  return `${locale === 'en' ? '' : `/${locale}`}/articles/${encodeURIComponent(slug)}`
}

export function currentLocale(): Locale {
  const resolved = i18n.resolvedLanguage?.split('-')[0]
  return isLocale(resolved) ? resolved : 'en'
}

await i18n
  .use(LanguageDetector)
  .use(resourcesToBackend(async (language: string) => {
    if (language === 'ko') await import('./fonts/ko.css')
    return import(`./locales/${language}.ts`).then((module) => module.default)
  }))
  .use(initReactI18next)
  .init({
    // Locale URLs are authoritative. Keeping `/` deterministically English
    // prevents crawler-dependent redirects and hydration mismatches.
    lng: typeof window === 'undefined' ? 'en' : localeFromPath(window.location.pathname) ?? 'en',
    fallbackLng: 'en',
    supportedLngs: [...locales],
    nonExplicitSupportedLngs: true,
    load: 'languageOnly',
    interpolation: { escapeValue: false },
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupFromPathIndex: 0,
      lookupLocalStorage: 'sidewisp-locale',
      // App.tsx persists the settled locale after URL/browser detection. Avoid
      // caching the deterministic SSR locale before the first client effect,
      // otherwise `/` would overwrite the browser preference with English.
      caches: [],
    },
    react: { useSuspense: false },
  })

export default i18n
